import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, EventEmitter, Output } from '@angular/core';
import { LoaderComponent } from '../../common-ui-components/loader/loader.component';
import { AgGridModule } from 'ag-grid-angular';
import { GridReadyEvent, GridApi, ChartModel } from 'ag-grid-community';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ExportService } from '../../services/export.service';
import { AgGridChartComponent } from '../../common-ui-components/ag-grid-chart/ag-grid-chart.component';

@Component({
  selector: 'app-report-visual-data',
  standalone: true,
  imports: [CommonModule, LoaderComponent, AgGridModule, AgGridChartComponent],
  templateUrl: './report-visual-data.component.html',
  styleUrls: ['./report-visual-data.component.css'],
})
export class ReportVisualDataComponent implements OnChanges, OnInit, OnDestroy {
  private readonly exportService = inject(ExportService);
  private readonly destroy$ = new Subject<void>();
  /**
   * Tracks the active export retry timeout. When export resources (grid/chart) aren't ready,
   * we retry with setTimeout. This ID allows us to cancel pending retries in ngOnDestroy,
   * preventing callbacks from running after the component is destroyed (avoids memory leaks
   * and "zombie" component behavior when user navigates away during export).
   */
  private exportRetryTimeoutId: ReturnType<typeof setTimeout> | null = null;
  /**
   * Set to true in ngOnDestroy. Used in handleExport to bail out early if a pending retry
   * callback fires after the component has been destroyed (race condition when destroy
   * happens between timeout scheduling and callback execution).
   */
  private isDestroyed = false;
  @Input() reportVisualType: string = 'TABLE';
  @Input() reportVisualData: any[] = [];
  @Input() reportVisualDataLoading: boolean = false;
  @Input() reportVisualChartType: string = 'bar';
  @Input() axisAttributesAndMeasures: any;
  @Input() savedGridFilterModel: any;
  @Input() savedGridSortModel: any;
  @Input() isCanvas = false;
  @Input() savedChartModel: any = null; // AG Grid ChartModel for restoring saved charts
  @Output() chartTypeChanged = new EventEmitter<string>();
  @Output() chartModelChanged = new EventEmitter<any>();
  @Output() filterChanged = new EventEmitter<any>();
  @Output() sortChanged = new EventEmitter<any>();

  public defaultColDef = {
    flex: 1,
    width: 100,
    resizable: true,
    filter: true, // Enable filtering globally
    sortable: true, // Enable sorting globally
  };

  // AG Grid integrated chart options (for TABLE view)
  public gridOptions: any = {
    enableCharts: true,
    enableRangeSelection: true,
    chartThemes: ['ag-default'],
    popupParent: document.body
  };

  // Grid options for hidden grid (used as data source for charts)
  public hiddenGridOptions: any = {
    enableCharts: true,
    enableRangeSelection: true,
    chartThemes: ['ag-default'],
    popupParent: document.body,
    suppressChartToolPanelsButton: true,
    chartMenuItems: (params: any) => {
      return params.defaultItems.filter(
        (item: any) => item !== 'chartUnlink' && item !== 'chartLink'
      );
    }
  };

  // AG Grid chart type variable (change this to switch chart types)
  public agGridChartType: string = 'bar'; // Default to bar chart
  
  public keys: string[] = [];
  public columnDefs: any = [];
  public rowData: any = [];
  public gridApi!: GridApi;
  public hiddenGridApi!: GridApi;
  private filterModel: any = null;
  private sortModel: any = null;
  public lastKnownChartModel: ChartModel | null = null;
  // Prevent stale parent-saved model from overwriting live user edits in chart setup.
  private hasLocalChartInteraction = false;

  public static sharedFilterModel: any = null;
  public static sharedSortModel: any = null;

  @ViewChild(AgGridChartComponent) agGridChartComponent?: AgGridChartComponent;

  constructor(){}

  ngOnInit(): void {
    if (this.savedChartModel) {
      this.lastKnownChartModel = this.savedChartModel;
    }
    
    this.exportService.$exportDataToFormat
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: string) => {
        try {
          // Check if it's JSON format (new format)
          const exportData = JSON.parse(data);
          this.handleExport(exportData.format, exportData.fileName, exportData.exportType);
        } catch {
          // Fallback to old format (just the format string)
          this.handleExport(data);
        }
      });
  }

  ngOnDestroy(): void {
    // Set first so any in-flight handleExport callbacks will no-op when they run
    this.isDestroyed = true;
    // Cancel pending export retries - prevents setTimeout callbacks from firing after destroy
    this.clearExportRetryTimeout();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Cancels any pending export retry timeout. Called before scheduling a new retry
   * (to avoid multiple timers) and in ngOnDestroy (to clean up on component teardown).
   */
  private clearExportRetryTimeout(): void {
    if (this.exportRetryTimeoutId !== null) {
      clearTimeout(this.exportRetryTimeoutId);
      this.exportRetryTimeoutId = null;
    }
  }

  public onHiddenGridReady(params: GridReadyEvent): void {
    this.hiddenGridApi = params.api;
    this.syncToHiddenGrid();
  }

  private syncToHiddenGrid(): void {
    if (!this.hiddenGridApi) return;

    if (this.filterModel && Object.keys(this.filterModel).length > 0) {
      this.hiddenGridApi.setFilterModel(this.filterModel);
    } else {
      this.hiddenGridApi.setFilterModel(null);
    }

    if (this.sortModel?.length > 0) {
      this.hiddenGridApi.applyColumnState({ state: this.sortModel, applyOrder: false });
    } else {
      this.hiddenGridApi.applyColumnState({ defaultState: { sort: null } });
    }
  }

  public onChartModelChange(chartModel: ChartModel): void {
    this.hasLocalChartInteraction = true;
    this.lastKnownChartModel = chartModel;
    this.chartModelChanged.emit(chartModel);
    if (chartModel.chartType) {
      this.agGridChartType = chartModel.chartType;
      this.chartTypeChanged.emit(chartModel.chartType);
    }
  }

  private handleExport(format: string, fileName?: string, exportType?: 'TABLE' | 'CHART' | 'BOTH', retryCount: number = 0): void {
    // Guard: if component was destroyed (e.g. user navigated away), a pending retry callback
    // may still fire - bail out to avoid operating on torn-down state
    if (this.isDestroyed) return;

    const maxRetriesTable = 20; // Max 2 seconds wait for table (20 * 100ms)
    const maxRetriesChart = 50; // Max 5 seconds wait for chart (50 * 100ms) - allows time for view switch + chart render

    // Use the same grid API selection logic as exportToFormat: in CHART view use hiddenGridApi
    const gridApiForTable = this.reportVisualType === 'CHART' ? this.hiddenGridApi : this.gridApi;

    // Check if required resources are ready based on export type
    if (exportType === 'TABLE' || this.reportVisualType === 'TABLE') {
      if (!gridApiForTable || !gridApiForTable?.getColumnDefs?.()) {
        if (retryCount < maxRetriesTable) {
          this.clearExportRetryTimeout(); // Clear any existing retry before scheduling a new one
          this.exportRetryTimeoutId = setTimeout(() => {
            this.exportRetryTimeoutId = null; // Clear before recursive call so clearExportRetryTimeout has nothing to clear
            this.handleExport(format, fileName, exportType, retryCount + 1);
          }, 100);
          return;
        }
      }
    }

    if (exportType === 'CHART' || exportType === 'BOTH') {
      // For chart/both export, we need hiddenGridApi and chart component
      const requiredGridApi = this.reportVisualType === 'CHART' ? this.hiddenGridApi : this.gridApi;

      if (!requiredGridApi || !this.agGridChartComponent) {
        if (retryCount < maxRetriesChart) {
          console.log(`Waiting for chart resources... retry ${retryCount + 1}/${maxRetriesChart}`);
          this.clearExportRetryTimeout(); // Clear any existing retry before scheduling a new one
          this.exportRetryTimeoutId = setTimeout(() => {
            this.exportRetryTimeoutId = null; // Clear before recursive call so clearExportRetryTimeout has nothing to clear
            this.handleExport(format, fileName, exportType, retryCount + 1);
          }, 100);
          return;
        } else {
          console.error('Chart export timeout: required resources not available');
          alert('Export failed: Chart is not ready. Please wait for the chart to load and try again.');
          return;
        }
      }
    }

    this.exportToFormat(format, fileName, exportType);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['savedGridFilterModel']?.currentValue) {
      this.filterModel = changes['savedGridFilterModel'].currentValue;
    }
    if (changes['savedGridSortModel']?.currentValue) {
      this.sortModel = changes['savedGridSortModel'].currentValue;
    }
    if (changes['savedChartModel']?.currentValue) {
      // Apply saved model on initial load. After user interacts with chart settings,
      // do not overwrite local state with potentially stale parent model.
      if (!this.hasLocalChartInteraction || !this.lastKnownChartModel) {
        this.lastKnownChartModel = changes['savedChartModel'].currentValue;
      }
    }

    if (this.reportVisualData.length > 0) {
      this.keys = Object.keys(this.reportVisualData[0]);
      this.updateTableData();
    }

    if (this.reportVisualType === 'TABLE' && this.gridApi) {
      this.gridApi.setFilterModel(this.filterModel);
      if (this.sortModel) {
        this.gridApi.applyColumnState({
          state: this.sortModel,
          applyOrder: false
        });
      }
    }

    if (changes['reportVisualType'] && this.reportVisualType === 'CHART') {
      this.syncToHiddenGrid();
    }
  }
   

  public static getCurrentGridState(): { filter: any; sort: any } {
    return {
      filter: ReportVisualDataComponent.sharedFilterModel,
      sort: ReportVisualDataComponent.sharedSortModel,
    };
  }

  private updateTableData() {
    this.columnDefs = this.keys.map((key) => {
      // Determine chart data type based on the first non-null value
      const sampleValue = this.reportVisualData.find(row => row[key] != null)?.[key];
      let chartDataType: 'category' | 'series' | 'excluded' = 'category';

      if (typeof sampleValue === 'number') {
        chartDataType = 'series';
      } else if (typeof sampleValue === 'string') {
        // Check if it's a date string
        const dateCheck = new Date(sampleValue);
        if (!isNaN(dateCheck.getTime())) {
          chartDataType = 'category'; // Treat dates as categories for now
        } else {
          chartDataType = 'category';
        }
      }

      return {
        headerName: key,
        valueGetter: (params: any) => params.data[key],
        field: key,
        filter: 'agTextColumnFilter', // Enable text filter for each column
        sortable: true, // Enable sorting for each column
        chartDataType: chartDataType // Add chart data type for AG Grid charts
      };
    });

    this.rowData = this.reportVisualData;
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
     const gridElement = document.querySelector('.ag-root-wrapper');
    
    if (gridElement) {
      // Prevent mouse events from bubbling up during column resize
      gridElement.addEventListener('mousedown', (e) => {
        if ((e.target as HTMLElement).classList.contains('ag-header-cell-resize')) {
          e.stopPropagation();
        }
      });
      
      gridElement.addEventListener('mousemove', (e) => {
        if ((e.target as HTMLElement).classList.contains('ag-header-cell-resize')) {
          e.stopPropagation();
        }
      });
    }
  
    // Restore filter and sort models if they exist
    if (this.filterModel) {
      console.log('Restoring filter model in onGridReady:', this.filterModel);
      this.gridApi.setFilterModel(this.filterModel);
    }
    if (this.sortModel) {
      console.log('Restoring sort model in onGridReady:', this.sortModel);
      this.gridApi.applyColumnState({
        state: this.sortModel,
        applyOrder: false // Prevent column reordering
      });
    }
    
  }  

  onFilterChanged() {
    if (this.gridApi) {
      const currentFilter = this.gridApi.getFilterModel();
      this.filterModel = currentFilter;
      ReportVisualDataComponent.sharedFilterModel = currentFilter;
      this.filterChanged.emit(currentFilter);

      if (this.hiddenGridApi) {
        this.hiddenGridApi.setFilterModel(currentFilter);
      }
    }
  }

  onSortChanged() {
    if (this.gridApi) {
      const currentSort = this.gridApi.getColumnState().filter(col => col.sort != null);
      this.sortModel = currentSort;
      ReportVisualDataComponent.sharedSortModel = currentSort;
      this.sortChanged.emit(currentSort);

      if (this.hiddenGridApi) {
        if (currentSort.length > 0) {
          this.hiddenGridApi.applyColumnState({ state: currentSort, applyOrder: false });
        } else {
          this.hiddenGridApi.applyColumnState({ defaultState: { sort: null } });
        }
      }
    }
  }

  public clearFiltersAndSorts(): void {
    this.filterModel = {};
    this.sortModel = [];
    ReportVisualDataComponent.sharedFilterModel = {};
    ReportVisualDataComponent.sharedSortModel = [];

    if (this.gridApi) {
      this.gridApi.setFilterModel(null);
      this.gridApi.applyColumnState({ defaultState: { sort: null } });
    }
    if (this.hiddenGridApi) {
      this.hiddenGridApi.setFilterModel(null);
      this.hiddenGridApi.applyColumnState({ defaultState: { sort: null } });
    }
  }

  public exportToFormat(format: string, fileName?: string, exportType?: 'TABLE' | 'CHART' | 'BOTH'): void {
    let gridApiToUse = this.reportVisualType === 'CHART' ? this.hiddenGridApi : this.gridApi;

    if (!gridApiToUse) {
      gridApiToUse = this.hiddenGridApi || this.gridApi;
    }

    this.exportService.exportToFormat(
      format,
      gridApiToUse,
      this.reportVisualType,
      fileName,
      exportType,
      this.agGridChartComponent
    );
  }

  public getChartModel(): ChartModel | null {
    if (this.agGridChartComponent) {
      const model = this.agGridChartComponent.getChartModel();
      if (model) {
        this.lastKnownChartModel = model;
        return model;
      }
      const lastKnown = this.agGridChartComponent.getLastKnownChartModel();
      if (lastKnown) {
        this.lastKnownChartModel = lastKnown;
        return lastKnown;
      }
    }
    return this.lastKnownChartModel;
  }

  public getAgGridChartComponent(): AgGridChartComponent | undefined {
    return this.agGridChartComponent;
  }

  public syncAndGetChartModel(): ChartModel | null {
    if (this.agGridChartComponent) {
      return this.agGridChartComponent.syncChartModel();
    }
    return null;
  }

}
